import { PantallaCargando } from '@/components/Cargando';
import LeadQuickSheet from '@/components/crm/LeadQuickSheet';
import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import SectionHelp from '@/components/help/SectionHelp';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import sensitiveActions from '@/help/sensitiveActions';
import CrmResumen from '@/components/crm/CrmResumen';
import { Trophy, XCircle, UserCheck, ExternalLink, GripVertical, AlertTriangle, Clock, Settings2, Plus } from 'lucide-react';

const STAGE_COLORS = ['bg-blue-50 border-blue-200', 'bg-cyan-50 border-cyan-200', 'bg-emerald-50 border-emerald-200', 'bg-amber-50 border-amber-200', 'bg-purple-50 border-purple-200', 'bg-pink-50 border-pink-200', 'bg-red-50 border-red-200'];
// Rediseño de tarjeta: cada tarjeta toma un color de acento (barra izquierda +
// avatar) derivado de su ORIGEN real (source_type/canal), no de la etapa —
// así una tarjeta se reconoce de un vistazo por de dónde vino el lead sin
// tener que leer texto, igual sea cual sea la columna en la que esté.
const ACCENT_BY_SOURCE = {
  whatsapp: { bar: 'bg-emerald-400', avatar: 'bg-emerald-100 text-emerald-700' },
  instagram_wall: { bar: 'bg-fuchsia-400', avatar: 'bg-fuchsia-100 text-fuchsia-700' },
  instagram: { bar: 'bg-fuchsia-400', avatar: 'bg-fuchsia-100 text-fuchsia-700' },
  facebook: { bar: 'bg-blue-400', avatar: 'bg-blue-100 text-blue-700' },
  messenger: { bar: 'bg-blue-400', avatar: 'bg-blue-100 text-blue-700' },
  voice: { bar: 'bg-cyan-400', avatar: 'bg-cyan-100 text-cyan-700' },
  web: { bar: 'bg-violet-400', avatar: 'bg-violet-100 text-violet-700' },
  manual: { bar: 'bg-slate-300', avatar: 'bg-slate-100 text-slate-600' },
};
const DEFAULT_ACCENT = { bar: 'bg-slate-300', avatar: 'bg-slate-100 text-slate-600' };

function initialsFor(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name[0].toUpperCase();
}

export default function CrmPipeline() {
  const [sheetLeadId, setSheetLeadId] = useState(null);
  const navigate = useNavigate();
  const { filterQuery } = useOutletContext() || {};
  const [pipeline, setPipeline] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedDealId, setDraggedDealId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [lossReasons, setLossReasons] = useState([]);
  const [lossModalDealId, setLossModalDealId] = useState(null);
  const [selectedLossReason, setSelectedLossReason] = useState('');
  const [showSlaConfig, setShowSlaConfig] = useState(false);
  const [slaDraft, setSlaDraft] = useState({});
  const [capiDraft, setCapiDraft] = useState({});
  const [savingSla, setSavingSla] = useState(false);
  // Lo reporta el resumen de arriba, que ya consulta /crm/status: no vale la
  // pena una segunda llamada solo para saber si hay que ensenar la guia.
  const [crmVacio, setCrmVacio] = useState(false);

  const load = async () => {
    try {
      const dealsUrl = `/tenant/crm/deals?status=all${filterQuery ? `&${filterQuery}` : ''}`;
      const [pRes, dRes, lrRes] = await Promise.all([
        api.get('/tenant/crm/pipelines/default'),
        api.get(dealsUrl),
        api.get('/tenant/crm/deals/loss-reasons'),
      ]);
      setPipeline(pRes.data);
      setDeals(dRes.data?.items || []);
      setLossReasons(lrRes.data?.items || []);
      const draft = {};
      const capi = {};
      (pRes.data?.stages || []).forEach(s => {
        draft[s.key] = s.max_days_in_stage != null ? String(s.max_days_in_stage) : '';
        capi[s.key] = s.capi_event || '';
      });
      setSlaDraft(draft);
      setCapiDraft(capi);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterQuery]);

  const moveStage = async (dealId, stageKey) => {
    const prevDeals = deals;
    setDeals(ds => ds.map(d => d.id === dealId ? { ...d, stage_key: stageKey } : d));
    try {
      await api.patch(`/tenant/crm/deals/${dealId}/stage`, { stage_key: stageKey });
    } catch (e) {
      setDeals(prevDeals);
      toast.error(formatApiError(e.response?.data?.detail) || 'Error al mover la oportunidad');
    }
  };

  const closeDeal = async (dealId, outcome, lossReason = null) => {
    try {
      const body = { outcome };
      if (outcome === 'lost') {
        if (!lossReason) {
          toast.error('Selecciona un motivo de pérdida');
          return;
        }
        body.loss_reason = lossReason;
      }
      await api.patch(`/tenant/crm/deals/${dealId}/close`, body);
      toast.success(outcome === 'won' ? 'Oportunidad ganada' : 'Oportunidad perdida');
      setLossModalDealId(null);
      setSelectedLossReason('');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const saveSlaConfig = async () => {
    if (!pipeline?.id) return;
    setSavingSla(true);
    try {
      const stages = (pipeline.stages || []).map(s => ({
        key: s.key,
        max_days_in_stage: slaDraft[s.key] === '' ? null : Number(slaDraft[s.key]),
        capi_event: capiDraft[s.key] || '',
      }));
      const res = await api.patch(`/tenant/crm/pipelines/${pipeline.id}/stages`, { stages });
      setPipeline(res.data);
      toast.success('Configuración de etapas guardada');
      setShowSlaConfig(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron guardar los límites');
    }
    setSavingSla(false);
  };

  const handleDragStart = (e, deal) => {
    setDraggedDealId(deal.id);
    e.dataTransfer.setData('text/plain', deal.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => { setDraggedDealId(null); setDragOverStage(null); };

  const handleDragOverStage = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageKey) setDragOverStage(stageKey);
  };

  const handleDropOnStage = (e, stageKey) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('text/plain') || draggedDealId;
    setDragOverStage(null);
    setDraggedDealId(null);
    if (!dealId) return;
    const deal = deals.find(d => d.id === dealId);
    if (!deal || deal.stage_key === stageKey) return;
    moveStage(dealId, stageKey);
  };

  if (loading) return <PantallaCargando />;
  if (!pipeline) return <EmptyStateGuide module="crm_pipeline" message="No hay pipeline configurado." />;

  const stages = pipeline.stages || [];

  return (
    <div className="space-y-3" data-testid="crm-pipeline">
      <SectionHelp module="crm_pipeline" title="el pipeline"
        description="Arrastra una tarjeta de oportunidad de una columna a otra para mover su etapa — se guarda automáticamente. Al cambiar de etapa puedes disparar automatizaciones si las configuraste. Si una oportunidad se queda mucho tiempo en una etapa, verás una alerta naranja."
        whatItDoesNot="no reordena las tarjetas dentro de la misma columna, solo cambia de etapa." />

      {/* Encabezado del Pipeline. Aquí vivía la pantalla «Inicio»: los números
          del CRM y el acceso a Entrantes. Se fusionó porque Inicio no hacía
          nada más que obligar a un clic extra para llegar al tablero. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <CrmResumen onSinDatos={setCrmVacio} />
        {/* En pantalla de teléfono las cifras ya ocupan varias filas: pegar
            estos dos al borde derecho los deja apretados contra el margen.
            Se alinean a la derecha solo cuando hay ancho de sobra. */}
        <div className="flex items-center gap-2 sm:ml-auto">
          <Button size="sm" className="h-8" data-testid="pipeline-nuevo-lead"
            onClick={() => navigate('/tenant/crm/leads?nuevo=1')}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo lead
          </Button>
          <button
            type="button"
            onClick={() => setShowSlaConfig(v => !v)}
            className="text-xs text-[#64748B] hover:text-[#0F172A] flex items-center gap-1"
            data-testid="pipeline-sla-toggle">
            <Settings2 className="w-3.5 h-3.5" /> Límites de tiempo y avisos a Meta
            <HelpTooltip text="Por cada etapa puedes hacer dos cosas, ambas opcionales: (1) poner un límite de días antes de marcar la oportunidad como atorada, y (2) avisarle a Meta (Facebook/Instagram) cuando una oportunidad llega a esa etapa, para medir el retorno de tus anuncios. Ejemplo: cuando María llega a «Agendó cita», Meta recibe el evento «Schedule»; cuando llega a «Ganada», recibe «Compra» con el monto." />
          </button>
        </div>
      </div>

      {crmVacio && (
        <EmptyStateGuide module="crm_home"
          message="Aún no tienes leads en el CRM. Configura Fuentes de Leads para recibirlos automáticamente, o agrega uno a mano."
          ctaLabel="Ir a Fuentes de Leads"
          onCta={() => navigate('/tenant/integrations/lead-sources')} />
      )}

      {showSlaConfig && (
        <div className="border rounded-lg p-3 bg-white space-y-2" data-testid="pipeline-sla-config">
          <p className="text-xs text-[#64748B]">
            Define cuántos días puede permanecer una oportunidad en cada etapa. Si se pasa del límite, se marca con alerta y puedes recibir una notificación vía Automatizaciones.
          </p>
          <p className="text-xs text-[#64748B]">
            <b>Aviso a Meta por etapa:</b> cuando una oportunidad llega a esta etapa, podemos avisarle a Meta
            (Facebook/Instagram) que ocurrió una conversión, para que tus anuncios aprendan y midas tu retorno
            real. Elige qué evento mandar en cada etapa (ej. «Agendó cita» o «Compra / Venta»).
            <HelpTooltip text="Para que el aviso salga, tu negocio debe tener conectada su cuenta de Meta Ads y un pixel elegido (en Marketing → Meta Ads). Si no la tienes, no pasa nada: simplemente no se manda, sin ningún error. El dato de contacto (correo/teléfono) viaja cifrado, nunca en texto plano." />
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {stages.map(stage => (
              <div key={stage.key} className="border rounded-lg p-2 space-y-1.5">
                <span className="block truncate font-medium text-xs">{stage.label}</span>
                <div className="flex items-center gap-2 text-xs">
                  <Input
                    type="number"
                    min="1"
                    placeholder="Sin límite"
                    className="h-7 w-24 text-xs"
                    value={slaDraft[stage.key] ?? ''}
                    onChange={e => setSlaDraft(d => ({ ...d, [stage.key]: e.target.value }))}
                    data-testid={`sla-input-${stage.key}`}
                  />
                  <span className="text-[#94A3B8]">días máx.</span>
                </div>
                <select
                  className="w-full h-7 text-xs border border-[#E2E8F0] rounded px-1.5 bg-white"
                  value={capiDraft[stage.key] ?? ''}
                  onChange={e => setCapiDraft(d => ({ ...d, [stage.key]: e.target.value }))}
                  data-testid={`capi-select-${stage.key}`}>
                  <option value="">Meta: no avisar</option>
                  <option value="Lead">Meta: Prospecto (Lead)</option>
                  <option value="Contact">Meta: Contacto</option>
                  <option value="Schedule">Meta: Agendó cita</option>
                  <option value="CompleteRegistration">Meta: Registro completo</option>
                  <option value="Purchase">Meta: Compra / Venta</option>
                </select>
              </div>
            ))}
          </div>
          <Button size="sm" onClick={saveSlaConfig} disabled={savingSla} data-testid="pipeline-sla-save">
            {savingSla ? 'Guardando...' : 'Guardar configuración de etapas'}
          </Button>
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-columns">
        {stages.map((stage, idx) => {
          const stageDeals = deals.filter(d => d.stage_key === stage.key && d.status === 'open');
          const isDropTarget = dragOverStage === stage.key;
          return (
            <div key={stage.key}
              onDragOver={(e) => handleDragOverStage(e, stage.key)}
              onDragLeave={() => setDragOverStage(prev => prev === stage.key ? null : prev)}
              onDrop={(e) => handleDropOnStage(e, stage.key)}
              className={`min-w-[248px] max-w-[288px] flex-shrink-0 border rounded-lg p-3 transition-colors ${STAGE_COLORS[idx % STAGE_COLORS.length]} ${isDropTarget ? 'ring-2 ring-[#0F172A] ring-offset-1' : ''}`}
              data-testid={`pipeline-stage-${stage.key}`}>
              <h4 className="text-[13px] font-bold mb-2.5 flex items-center justify-between gap-1">
                <span className="truncate">{stage.label}</span>
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  {stage.max_days_in_stage ? (
                    <span className="text-[9px] font-normal text-amber-700 flex items-center gap-0.5" title={`Límite: ${stage.max_days_in_stage} días`}>
                      <Clock className="w-2.5 h-2.5" />{stage.max_days_in_stage}d
                    </span>
                  ) : null}
                  <span className="text-[10px] font-bold text-[#475569] bg-white/70 rounded-full min-w-[18px] text-center px-1.5 py-0.5">{stageDeals.length}</span>
                </span>
              </h4>
              <div className="space-y-2.5 min-h-[40px]">
                {stageDeals.map(deal => {
                  const accent = ACCENT_BY_SOURCE[deal.channel] || ACCENT_BY_SOURCE[deal.medium] || DEFAULT_ACCENT;
                  const name = deal.lead_display_name || deal.title;
                  // El título "Oportunidad: <mismo nombre>" es puro relleno cuando
                  // se autogeneró al crear el lead — solo se muestra si de verdad
                  // dice algo distinto (ej. un título editado a mano).
                  const showTitle = deal.title && deal.title.trim() !== `Oportunidad: ${name}`.trim() && deal.title.trim() !== name.trim();
                  return (
                  <div key={deal.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal)}
                    onDragEnd={handleDragEnd}
                    onClick={() => { if (deal.crm_lead_id) setSheetLeadId(deal.crm_lead_id); }}
                    className={`group relative bg-white rounded-xl shadow-sm border overflow-hidden cursor-grab active:cursor-grabbing transition-all hover:shadow-md ${draggedDealId === deal.id ? 'opacity-40' : ''} ${deal.stage_sla_breached ? 'border-amber-300' : 'border-[#E2E8F0]'}`}
                    data-testid={`deal-card-${deal.id}`}>
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${accent.bar}`} />
                    <div className="pl-3 pr-2.5 py-2.5 space-y-2">
                      {deal.stage_sla_breached && (
                        <div className="-mx-0.5 -mt-0.5 mb-1 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-[10px] text-amber-800 flex items-center gap-1 font-medium" data-testid={`deal-sla-alert-${deal.id}`}>
                          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                          Atorada {deal.days_in_stage}d{deal.max_days_in_stage ? ` (límite ${deal.max_days_in_stage}d)` : ''}
                          <HelpTooltip text="Esta oportunidad lleva más tiempo del permitido en esta etapa. Revísala o muévela a la siguiente etapa." />
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-3.5 h-3.5 text-[#CBD5E1] flex-shrink-0 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity -ml-1" />
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${accent.avatar}`}>
                          {initialsFor(name)}
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                          {deal.lead_display_name ? (
                            <button onClick={(e) => { e.stopPropagation(); setSheetLeadId(deal.crm_lead_id); }}
                              className="text-sm font-semibold text-[#0F172A] hover:text-[#0F172A] hover:underline truncate flex items-center gap-1 w-full text-left" data-testid={`deal-lead-link-${deal.id}`}>
                              <span className="truncate">{name}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0 text-[#94A3B8] opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          ) : (
                            <p className="text-sm font-semibold text-[#0F172A] truncate">{name}</p>
                          )}
                          {showTitle && <p className="text-[11px] text-[#64748B] truncate">{deal.title}</p>}
                        </div>
                      </div>

                      {deal.vehicle && (deal.vehicle.brand || deal.vehicle.model || deal.vehicle.license_plate) && (
                        <p className="text-[10px] text-[#64748B] truncate pl-9" data-testid={`deal-vehicle-${deal.id}`}>
                          {[deal.vehicle.brand, deal.vehicle.model, deal.vehicle.license_plate].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {(deal.channel || deal.campaign_name) && (
                        <p className="text-[10px] text-[#94A3B8] truncate pl-9">
                          {[deal.channel, deal.campaign_name].filter(Boolean).join(' · ')}
                        </p>
                      )}

                      <div className="flex items-center justify-between gap-2 pl-9">
                        {deal.value_estimate ? (
                          <span className="inline-flex items-center text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                            {deal.currency} {deal.value_estimate.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-[10px] text-[#CBD5E1] italic">Sin valor</span>
                        )}
                        {deal.assigned_to_name && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-[#334155] bg-[#F1F5F9] rounded-full px-2 py-0.5 truncate max-w-[110px]" data-testid={`deal-assignee-${deal.id}`}>
                            <UserCheck className="w-2.5 h-2.5 flex-shrink-0 text-[#64748B]" /> <span className="truncate">{deal.assigned_to_name}</span>
                          </span>
                        )}
                      </div>
                      {deal.assignment_pool_name && (
                        <p className="text-[10px] text-blue-500 truncate pl-9" data-testid={`deal-pool-${deal.id}`}>{deal.assignment_pool_name}</p>
                      )}

                      <div className="flex gap-1 pt-1.5 mt-1 border-t border-[#F1F5F9] pl-9">
                        <SensitiveActionHelp action={sensitiveActions.crm_close_deal_won} onConfirm={() => closeDeal(deal.id, 'won')}>
                          <button className="flex-1 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50 rounded-md py-1 flex items-center justify-center gap-1 transition-colors">
                            <Trophy className="w-3 h-3" /> Ganada
                          </button>
                        </SensitiveActionHelp>
                        <button
                          className="flex-1 text-[10px] font-medium text-red-600 hover:bg-red-50 rounded-md py-1 flex items-center justify-center gap-1 transition-colors"
                          onClick={() => { setLossModalDealId(deal.id); setSelectedLossReason(''); }}
                          data-testid={`deal-mark-lost-${deal.id}`}>
                          <XCircle className="w-3 h-3" /> Perdida
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
                {stageDeals.length === 0 && <p className="text-[10px] text-[#94A3B8] italic">Sin oportunidades</p>}
              </div>
            </div>
          );
        })}
      </div>

      {deals.filter(d => d.status === 'won' || d.status === 'lost').length > 0 && (
        <div className="text-xs text-[#64748B] mt-4">
          <h4 className="font-bold mb-1">Cerradas</h4>
          {deals.filter(d => d.status !== 'open' && d.status !== 'archived').map(d => (
            <div key={d.id} className="flex items-center gap-2 py-1">
              {d.status === 'won' ? <Trophy className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-400" />}
              <span>{d.title}</span>
              <span className="text-[#94A3B8]">({d.status === 'won' ? 'Ganada' : 'Perdida'})</span>
              {d.loss_reason_label && <span className="text-[#94A3B8]">— {d.loss_reason_label}</span>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!lossModalDealId} onOpenChange={(open) => { if (!open) { setLossModalDealId(null); setSelectedLossReason(''); } }}>
        <DialogContent data-testid="deal-loss-reason-modal">
          <DialogHeader>
            <DialogTitle>Motivo de pérdida</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Indica por qué se perdió esta oportunidad. Esto ayuda a entender qué mejorar en tu proceso de ventas — no se puede dejar en blanco.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-1 text-xs font-medium">
              Motivo
              <HelpTooltip text="Elige la razón más cercana. Si ninguna aplica exactamente, usa 'Otro motivo'. Esta información aparece en el historial de oportunidades cerradas." />
            </div>
            <Select value={selectedLossReason} onValueChange={setSelectedLossReason}>
              <SelectTrigger className="h-9 text-xs" data-testid="deal-loss-reason-select">
                <SelectValue placeholder="Selecciona un motivo..." />
              </SelectTrigger>
              <SelectContent>
                {lossReasons.map(r => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setLossModalDealId(null); setSelectedLossReason(''); }}>
                Cancelar
              </Button>
              <Button size="sm" onClick={() => closeDeal(lossModalDealId, 'lost', selectedLossReason)} data-testid="deal-loss-reason-confirm">
                Confirmar pérdida
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    
      <LeadQuickSheet
        leadId={sheetLeadId}
        open={!!sheetLeadId}
        onClose={() => setSheetLeadId(null)}
      />
</div>
);
}